import { Effect, Exit, Ref, Schema, type Scope } from "effect";
import { stationInvalid } from "@workerstation/shared/errors/station";
import { workspaceInvalid } from "@workerstation/shared/errors/workspace-http";
import type { AcceptedWork } from "../accepted-work.ts";
import type { StationApi } from "../../api/station/service.ts";
import type { StationClientFailure, WorkspaceSnapshot } from "../../api/station/model.ts";
import { WorkspaceDraft, WorkspaceTarget } from "./persistence-model.ts";
import type { StationPersistence } from "./persistence-service.ts";
import type { StationSession } from "./session-service.ts";
import type { PendingSaveSession } from "./pending-service.ts";
import type { WorkbenchQueries } from "./workbench-queries.ts";
import type { StationBrowserIo } from "./workbench-service.ts";
import type { StationWorkbenchSnapshot } from "./workbench-model.ts";
import { workspaceView } from "./workspace-view.ts";

export interface WorkbenchPorts {
  readonly api: typeof StationApi.Service;
  readonly io: typeof StationBrowserIo.Service;
  readonly persistence: typeof StationPersistence.Service;
  readonly station: typeof StationSession.Service;
  readonly accepted: typeof AcceptedWork.Service;
}
export interface CreatedWorkspace { readonly id: string; readonly target: WorkspaceTarget; readonly draft: WorkspaceDraft; readonly persisted: boolean }
export const makeWorkbenchCore = (ports: WorkbenchPorts, root: Scope.Scope, owner: Scope.Scope,
  state: Ref.Ref<StationWorkbenchSnapshot>, pending: PendingSaveSession, queries: WorkbenchQueries,
  publish: Effect.Effect<void>, retain: (result: Exit.Exit<unknown, unknown>) => Effect.Effect<void>
) => {
  const read = Ref.get(state);
  const active = (sequence?: number) => read.pipe(Effect.map(current => !current.closed && root.state._tag !== "Closed" && owner.state._tag !== "Closed"
    && (sequence === undefined || sequence === current.navigationSequence)));
  const update = (change: (current: StationWorkbenchSnapshot) => StationWorkbenchSnapshot) => Ref.update(state, current => current.closed ? current : change(current)).pipe(Effect.andThen(publish));
  const target = (workspaceId: string): Effect.Effect<WorkspaceTarget, StationClientFailure> => read.pipe(Effect.flatMap(current =>
    Schema.decodeUnknownEffect(WorkspaceTarget)({ stationId: current.stationId, workspaceId }).pipe(Effect.mapError(() => workspaceInvalid("Invalid workbench target.")))));
  const readAccepted = <A, E, R>(operation: Effect.Effect<A, E, R>) => Effect.gen(function* () {
    const result = yield* Effect.exit(Effect.scoped(operation)); yield* retain(result); return yield* result;
  });
  const hydrate = Effect.fn("Frontend.StationWorkbench.hydrate")(function* (identity: WorkspaceTarget, source: WorkspaceSnapshot | WorkspaceDraft, preserve = false, fresh = false) {
    if (!(yield* active())) return;
    yield* ports.persistence.observeVersion(identity, "updatedAt" in source ? {
      updatedAt: source.updatedAt, ...(source.refRevision === undefined ? {} : { refRevision: source.refRevision }),
      ...(source.commitId === undefined ? {} : { commitId: source.commitId })
    } : {}, preserve);
    yield* pending.discard();
    const view = workspaceView(source);
    yield* update(current => ({ ...current, workspaceId: identity.workspaceId, activeWorkspace: identity.workspaceId, draft: view, phase: "idle",
      hydration: { sequence: (current.hydration?.sequence ?? 0) + 1, workspaceId: identity.workspaceId, previousWorkspaceId: current.workspaceId, view },
      freshPreviewSequence: current.freshPreviewSequence + (fresh ? 1 : 0) }));
  });
  const create = Effect.fn("Frontend.StationWorkbench.create")(function* (input: WorkspaceDraft) {
    const current = yield* read;
    if (current.stationId === null) return yield* Effect.fail(stationInvalid("Station is unavailable."));
    const id = yield* ports.io.newWorkspaceId;
    const identity = yield* Schema.decodeUnknownEffect(WorkspaceTarget)({ stationId: current.stationId, workspaceId: id }).pipe(
      Effect.mapError(() => workspaceInvalid("Invalid new workerspace target.")));
    const draft = yield* Schema.decodeUnknownEffect(WorkspaceDraft)(input).pipe(Effect.mapError(() => workspaceInvalid("Invalid new workerspace draft.")));
    if (current.workspaceId !== null) {
      const source = yield* Schema.decodeUnknownEffect(WorkspaceTarget)({ stationId: current.stationId, workspaceId: current.workspaceId }).pipe(
        Effect.mapError(() => workspaceInvalid("Invalid workerspace version source.")));
      yield* ports.persistence.copyRepositoryVersion(source, identity);
    }
    const result = yield* Effect.exit(ports.persistence.save(identity, draft));
    if (Exit.isFailure(result)) yield* ports.persistence.discardRepositoryVersion(identity);
    return { id, target: identity, draft, persisted: Exit.isSuccess(result) } satisfies CreatedWorkspace;
  });
  return { ...ports, root, owner, state, read, active, update, target, hydrate, pending, queries, publish, create, readAccepted };
};
export type WorkbenchCore = ReturnType<typeof makeWorkbenchCore>;
